import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

export interface WishlistTour {
  id: string; // This corresponds to tourId in schema
  tourId: string; // redundant but to match schema if needed
  name: string;
  image: string;
  // Extra fields for UI not in schema return type
  description?: string;
  href?: string;
}

const WISHLIST_STORAGE_KEY = 'ecoplacetours-wishlist';

export function useWishlist() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: wishlistItems = [] } = useQuery({
    queryKey: ['local-wishlist'],
    queryFn: async () => {
      // Retrieve instantly from local storage instead of backend API
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!stored) return [];
      try {
        return JSON.parse(stored) as WishlistTour[];
      } catch (err) {
        console.error("Error parsing wishlist:", err);
        return [];
      }
    }
  });

  const addToWishlistMutation = useMutation({
    mutationFn: async (vars: { tour: WishlistTour; userName?: string; userEmail?: string }) => {
      const { tour } = vars;
      const current = [...wishlistItems];
      if (!current.some(t => t.id === tour.id)) {
        current.push(tour);
        localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(current));
      }
      return current;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-wishlist'] });
      toast({
        title: "Added to wishlist",
        description: "We've securely saved this tour to your wishlist.",
      });
    },
    onError: (error) => {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to save to wishlist.",
        variant: "destructive",
      });
    }
  });

  const removeFromWishlistMutation = useMutation({
    mutationFn: async (tourId: string) => {
      const filtered = wishlistItems.filter(t => t.id !== tourId);
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(filtered));
      return filtered;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['local-wishlist'] });
      toast({
        title: "Removed from wishlist",
        description: "Tour has been completely removed from your wishlist.",
      });
    }
  });

  const addToWishlist = (tour: WishlistTour, userName?: string, userEmail?: string) => {
    addToWishlistMutation.mutate({ tour, userName, userEmail });
  };

  const removeFromWishlist = (tourId: string) => {
    removeFromWishlistMutation.mutate(tourId);
  };

  const isInWishlist = (tourId: string) => {
    return wishlistItems.some((item: any) => item.id === tourId);
  };

  return {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    isInWishlist
  };
}
